import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useRequestCancelDealerRetailMutation,
    useApproveOrRejectCancelRetailMutation
} from '../../../../api/endpoints/parts';

const NOTE_MAX_LENGTH = 500;

const RetailCancelDialog = ({ visible, mode = 'request', retailData, onHide, onSuccess }) => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [formState, setFormState] = useState({
        cancelNote: '',
        rejectNote: '',
        rejectMode: false
    });
    const { cancelNote, rejectNote, rejectMode } = formState;

    const [requestCancel, { isLoading: isRequesting }] = useRequestCancelDealerRetailMutation();
    const [approveOrRejectCancel, { isLoading: isProcessing }] = useApproveOrRejectCancelRetailMutation();

    useEffect(() => {
        if (visible) {
            setFormState({ cancelNote: '', rejectNote: '', rejectMode: false });
        }
    }, [visible]);

    if (!retailData) return null;

    const isRequestMode = mode === 'request';

    const headerLabel = isRequestMode
        ? th('retailSales.cancelRequestTitle')
        : th('retailSales.cancelApprovalTitle');

    const handleRequestCancel = async () => {
        try {
            await requestCancel({
                retailId: retailData.id,
                cancelNote: cancelNote?.trim() || null
            }).unwrap();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('retailSales.cancelRequestSuccess'),
                life: 3000
            });
            onSuccess?.();
            setTimeout(() => onHide(), 800);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    };

    const handleApproveCancel = async () => {
        try {
            await approveOrRejectCancel({
                retailId: retailData.id,
                approveCancelDealerRetailMessage: 1,
                rejectNote: null
            }).unwrap();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('retailSales.cancelApproveSuccess'),
                life: 3000
            });
            onSuccess?.();
            setTimeout(() => onHide(), 800);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    };

    const handleRejectCancel = async () => {
        if (!rejectNote?.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: th('retailSales.rejectNoteRequired'),
                life: 3000
            });
            return;
        }
        try {
            await approveOrRejectCancel({
                retailId: retailData.id,
                approveCancelDealerRetailMessage: 2,
                rejectNote: rejectNote.trim()
            }).unwrap();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('retailSales.cancelRejectSuccess'),
                life: 3000
            });
            onSuccess?.();
            setTimeout(() => onHide(), 800);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    };

    const dateFormatter = (val) => (val ? new Date(val).toLocaleString('tr-TR') : '-');

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={th('common.close')}
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
                disabled={isRequesting || isProcessing}
            />
            {isRequestMode && (
                <Button
                    label={th('retailSales.cancelRequestSubmit')}
                    icon="pi pi-send"
                    severity="danger"
                    onClick={handleRequestCancel}
                    loading={isRequesting}
                    disabled={isRequesting}
                />
            )}
            {!isRequestMode && !rejectMode && (
                <>
                    <Button
                        label={th('retailSales.rejectCancel')}
                        icon="pi pi-times"
                        severity="danger"
                        onClick={() => setFormState((s) => ({ ...s, rejectMode: true }))}
                        disabled={isProcessing}
                    />
                    <Button
                        label={th('retailSales.approveCancel')}
                        icon="pi pi-check"
                        severity="success"
                        onClick={handleApproveCancel}
                        loading={isProcessing}
                        disabled={isProcessing}
                    />
                </>
            )}
            {!isRequestMode && rejectMode && (
                <>
                    <Button
                        label={th('common.back')}
                        icon="pi pi-arrow-left"
                        onClick={() => setFormState((s) => ({ ...s, rejectMode: false, rejectNote: '' }))}
                        className="p-button-text"
                        disabled={isProcessing}
                    />
                    <Button
                        label={th('retailSales.rejectCancelSubmit')}
                        icon="pi pi-send"
                        severity="danger"
                        onClick={handleRejectCancel}
                        loading={isProcessing}
                        disabled={isProcessing}
                    />
                </>
            )}
        </div>
    );

    const renderInfoRow = (label, value) => (
        <div className="flex justify-content-between mb-2">
            <span className="font-semibold">{label}</span>
            <span>{value || '-'}</span>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={headerLabel}
                style={{ width: '90vw', maxWidth: 600 }}
                footer={footer}
                closeOnEscape={!isRequesting && !isProcessing}
                closable={!isRequesting && !isProcessing}
            >
                <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    {renderInfoRow(th('retailSales.invoiceSeriNo'), retailData.invoiceSeriNo)}
                    {renderInfoRow(
                        th('retailSales.invoiceDate'),
                        retailData.invoiceDate ? new Date(retailData.invoiceDate).toLocaleDateString('tr-TR') : '-'
                    )}
                    {renderInfoRow(th('retailSales.customer'), retailData.customer)}
                    {renderInfoRow(
                        th('retailSales.netTotalAmount'),
                        retailData.netTotalAmount != null
                            ? Number(retailData.netTotalAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })
                            : '-'
                    )}
                </div>

                {!isRequestMode && (
                    <div className="mt-3 p-3" style={{ backgroundColor: '#fff7e6', borderRadius: '6px', borderLeft: '4px solid #f59e0b' }}>
                        {renderInfoRow(th('retailSales.cancelRequestedBy'), retailData.cancelRequestedName)}
                        {renderInfoRow(
                            th('retailSales.cancelRequestedDate'),
                            dateFormatter(retailData.cancelRequestedDate)
                        )}
                        <div className="mt-2">
                            <div className="font-semibold mb-1">{th('retailSales.cancelRequestNote')}</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{retailData.cancelRequestNote || '-'}</div>
                        </div>
                    </div>
                )}

                {isRequestMode && (
                    <div className="mt-3">
                        <label className="block mb-2 font-semibold">{th('retailSales.cancelNote')}</label>
                        <InputTextarea
                            value={cancelNote}
                            onChange={(e) => setFormState((s) => ({ ...s, cancelNote: e.target.value }))}
                            rows={4}
                            maxLength={NOTE_MAX_LENGTH}
                            placeholder={th('retailSales.cancelNotePlaceholder')}
                            className="w-full"
                            disabled={isRequesting}
                        />
                        <small className="text-500">
                            {cancelNote.length}/{NOTE_MAX_LENGTH}
                        </small>
                        <div className="mt-3 p-2" style={{ backgroundColor: '#fff7e6', borderRadius: '6px', borderLeft: '4px solid #f59e0b' }}>
                            <small className="text-orange-700">{th('retailSales.cancelConfirmMessage')}</small>
                        </div>
                    </div>
                )}

                {!isRequestMode && rejectMode && (
                    <div className="mt-3">
                        <label className="block mb-2 font-semibold">
                            {th('retailSales.rejectNote')} <span className="text-red-500">*</span>
                        </label>
                        <InputTextarea
                            value={rejectNote}
                            onChange={(e) => setFormState((s) => ({ ...s, rejectNote: e.target.value }))}
                            rows={4}
                            maxLength={NOTE_MAX_LENGTH}
                            placeholder={th('retailSales.rejectNotePlaceholder')}
                            className="w-full"
                            disabled={isProcessing}
                        />
                        <small className="text-500">
                            {rejectNote.length}/{NOTE_MAX_LENGTH}
                        </small>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default RetailCancelDialog;
